<template>
  <div v-bind="canvasAttrs" style="position: relative;">
    <highlightCanvas
      v-bind="{ overlayAttributesSimple, pageNumber, scale, selecting }"
      @selectExcerpt="$emit('selectExcerpt', $event)"
    ></highlightCanvas>
    <canvas
      :id="`render-${pageNumber}`"
      ref="render"
      v-visible.once="drawPage"
      v-bind="overlayAttributes"
      class="overlay-page page-canvas"
    ></canvas>
    <ScreenCapCanvas
      class="pdf-preview"
      v-bind="{ overlayAttributesSimple, pageNumber, scale }"
    ></ScreenCapCanvas>
    <div
      :id="`textLayer-${pageNumber}`"
      ref="textLayer"
      @mouseup="toggleExcerptBtn"
      @selectstart="listenForSelection"
      v-touch:end="toggleExcerptBtn"
      v-bind="overlayAttributes"
      v-resize.quiet="onResize"
      :class="{ 'overlay-page': true, textLayer: true, screenShot: isScreenCapEnabled }"
    ></div>
    <v-btn
      v-if="addBtn.show"
      @click="saveExcerpt"
      color="blue lighten-1"
      :style="`position: absolute; left: ${addBtn.left}px; top: ${addBtn.top}px; z-index: 4;`"
      >CAPTURE EXCERPT</v-btn
    >
    <v-btn
      v-if="isScreenCapEnabled && capturedImage"
      @click="$emit('saveScreen')"
      color="blue lighten-1"
      :style="
        `position: absolute; margin-left: -50px; left: ${finalShape.minX +
          finalShape.width / 2}px; top: ${finalShape.minY + finalShape.height}px; z-index: 4;`
      "
      >CAPTURE</v-btn
    >
  </div>
</template>

<script>
import debug from 'debug';
const log = debug('app:components/PDFPage');

import debounce from 'lodash/debounce';

import { PIXEL_RATIO } from './utils/constants';
import visible from './directives/visible';
import PDFJS from 'pdfjs-dist';
import ScreenCapCanvas from './ScreenCapCanvas';
import HighlightCanvas from './HighlightCanvas';
import { mapState, mapMutations, mapActions } from 'vuex';

export default {
  name: 'PDFPage',
  data() {
    return {
      viewport: this.page.getViewport(this.optimalScale),
      pagePosition: {},
      selecting: false,
      addBtn: {
        show: false,
        test: '',
        left: null,
        right: null
      },
      log(c) {
        console.log(c);
      }
    };
  },
  components: {
    ScreenCapCanvas,
    HighlightCanvas
  },
  props: {
    page: {
      type: Object, // instance of PDFPageProxy returned from pdf.getPage
      required: true
    },
    scale: {
      type: Number,
      required: true
    },
    optimalScale: {
      type: Number,
      required: true
    },
    isPageFocused: {
      type: Boolean,
      default: false
    },
    isElementFocused: {
      type: Boolean,
      default: false
    }
  },

  directives: {
    visible
  },
  created() {
    this.onResize = debounce(this.updateTextLayer.bind(this), 500);
  },
  computed: {
    ...mapState('components/pdfViewer', [
      'captureCurrent',
      'finalShape',
      'isScreenCapEnabled',
      'capturedImage'
    ]),
    actualSizeViewport() {
      const viewport = this.viewport.clone({ scale: this.scale });
      // console.log('standardViewport', this.viewport);
      // console.log('Actual viewport', viewport);
      return viewport;
    },

    anotherViewport() {
      const newScale = this.scale / PIXEL_RATIO;
      // console.log('newScale', newScale);
      const viewport = this.viewport.clone({ scale: newScale });
      // console.log('anotherViewport', viewport);

      return viewport;
    },

    canvasStyle() {
      const { width: actualSizeWidth, height: actualSizeHeight } = this.actualSizeViewport;
      const [pixelWidth, pixelHeight] = [actualSizeWidth, actualSizeHeight].map(dim =>
        Math.ceil(dim / PIXEL_RATIO)
      );

      return `width: ${pixelWidth}px; height: ${pixelHeight}px;`;
    },

    canvasAttrs() {
      let { width, height } = this.viewport;
      [width, height] = [width, height].map(dim => Math.ceil(dim));
      const style = this.canvasStyle;

      return {
        width,
        height,
        style,
        class: 'pdf-page box-shadow'
      };
    },

    overlayAttributesSimple() {
      // console.log(`overlayAttributes page: ${this.pageNumber}`);
      const { width: actualSizeWidth, height: actualSizeHeight } = this.actualSizeViewport;
      const [pixelWidth, pixelHeight] = [actualSizeWidth, actualSizeHeight].map(dim =>
        Math.ceil(dim / PIXEL_RATIO)
      );
      let style = this.canvasStyle;
      const parent = this.$el;
      if (parent) {
        style += `offsetTop: ${parent.offsetTop}; offsetLeft: ${parent.offsetLeft}`;
      }
      return {
        width: pixelWidth,
        height: pixelHeight,
        style
      };
    },

    overlayAttributes() {
      // console.log(`overlayAttributes page: ${this.pageNumber}`);
      let { width, height } = this.viewport;
      [width, height] = [width, height].map(dim => Math.ceil(dim));
      let style = this.canvasStyle;
      const parent = this.$el;
      if (parent) {
        style += `offsetTop: ${parent.offsetTop}; offsetLeft: ${parent.offsetLeft}`;
      }
      return {
        width,
        height,
        style
      };
    },

    pageNumber() {
      return this.page.pageNumber;
    }
  },

  methods: {
    ...mapMutations('components/pdfViewer', [
      'setCaptureCurrent',
      'setCapturedImage',
      'setPagePosition'
    ]),
    ...mapActions('components/pdfViewer', ['addSelectionToResourceExcerpt']),
    listenForSelection() {
      const removeListeners = () => {
        console.log('remove listeners');
        document.removeEventListener('selectionchange', this.toggleExcerptBtn);
        document.removeEventListener('mousedown', removeListeners);
        document.removeEventListener('mouseup', removeListeners);
        this.selecting = false;
      };
      removeListeners();
      console.log('Selection started in targetDiv');
      document.addEventListener('selectionchange', this.toggleExcerptBtn);
      document.addEventListener('mousedown', removeListeners);
      document.addEventListener('mouseup', removeListeners);
      this.selecting = true;
    },
    focusPage() {
      if (this.isPageFocused) return;

      this.$emit('page-focus', this.pageNumber);
    },
    toggleExcerptBtn() {
      const selection = window.getSelection();
      console.log('togggle btn', selection);
      if (selection.rangeCount > 0 && selection.toString().length > 0) {
        console.log(selection, selection.toString());

        const pageRange = [];
        try {
          const firstPageNum = selection.anchorNode.parentNode.parentNode.id.split('-')[1];
          const lastPageNum = selection.extentNode.parentNode.parentNode.id.split('-')[1];
          console.log(firstPageNum, lastPageNum);
          pageRange.push(firstPageNum);
          if (lastPageNum != firstPageNum) pageRange.push(lastPageNum);
        } catch (e) {
          console.log(e);
          pageRange.push(this.pageNumber);
        }
        const getRange = selection.getRangeAt(0);
        const getRect =
          pageRange.length == 1
            ? getRange.getBoundingClientRect()
            : getRange.endContainer.parentNode.getBoundingClientRect();
        const clientRects = getRange.getClientRects();
        console.log(pageRange);
        console.log(clientRects);
        const lastPage =
          pageRange.length == 1
            ? this.$refs.textLayer
            : document.getElementById('textLayer-' + pageRange[1]);
        console.log(lastPage);
        const pageRect = lastPage.getBoundingClientRect();

        const left = getRect.left - pageRect.left + getRect.width / 2 - 30;
        const top = getRect.bottom - pageRect.top + 10;
        this.addBtn = {
          show: true,
          left: left,
          top: top,
          text: selection.toString(),
          clientRects,
          pageRange
        };
      } else {
        this.addBtn.show = false;
        this.addBtn.text = '';
      }
    },
    saveExcerpt() {
      const { text, clientRects, pageRange } = this.addBtn;
      // console.log('selection', selection);
      console.log('text', text);
      // console.log('anchorNode', selection.anchorNode);
      // console.log('anchorOffset', selection.anchorOffset);
      // console.log('anchorNode', selection.focusNode);
      // console.log('anchorOffset', selection.focusOffset);

      console.log('stylizing');
      // var range = window.getSelection().getRangeAt(0);
      // const clientRects = range.getClientRects();
      // console.log('clientRects', clientRects);
      let newRect = [];
      for (let i = 0; i < clientRects.length; i++) {
        newRect.push(clientRects[i]);
      }

      let toKeep = null;
      const smallerRect = [];
      console.log('newRect', newRect);
      newRect
        .sort((a, b) => a.x - b.x)
        .forEach((item, index) => {
          console.log('item', item);
          console.log('index', index);
          if (toKeep === null) {
            toKeep = item;
            if (index + 1 == newRect.length) {
              console.log('push toKeep to smallerRect', toKeep);
              smallerRect.push(toKeep);
            }
            return;
          }

          if (item.x !== toKeep.x || item.width !== toKeep.width) {
            smallerRect.push(toKeep);
            toKeep = item;
          } else {
            if (item.height > toKeep.height) {
              toKeep = item;
            }
          }
          console.log('index', index, 'newRectLength', newRect.length);
          if (index + 1 == newRect.length) {
            console.log('push toKeep to smallerRect', toKeep);
            smallerRect.push(toKeep);
          }
        });

      console.log('smallerRect', smallerRect);

      const el = document.getElementsByClassName('pdf-viewer__document')[0];
      const viewPortToPDFViewer = { x: el.scrollLeft, y: el.scrollTop };
      console.log('viewPortToPDFViewer', viewPortToPDFViewer);

      const smallerRectAbsolute = smallerRect;
      //.map(e => {
      //   const returnElement = e;

      //   returnElement.x = returnElement.x + viewPortToPDFViewer.x;
      //   returnElement.y = returnElement.y + viewPortToPDFViewer.y;
      //   return returnElement;
      // });
      //this.setCapturedText({ text, positions: smallerRectAbsolute });
      // this.addSelectionToResourceExcerpt({ text, positions: smallerRectAbsolute, pageRange });
      const positions = smallerRectAbsolute;
      const pageArray = pageRange.map(p => {
        const rect = document.getElementById('textLayer-' + p).getBoundingClientRect();
        const { x, y, width, height, top, right, bottom, left } = rect;
        return { top, right, bottom, left, width, height, x, y, page: Number(p) };
      });
      console.log(pageArray);
      //const pageArray = Object.values(state.pagePositions);
      //created saved selection object which has the text and the positions on the page of the elements
      const savedSelection = {};
      savedSelection.pages = [];
      savedSelection.positions = [];
      savedSelection.text = text;
      if (pageRange.length > 1) {
        for (var i = pageRange[0]; i <= pageRange[1]; i++) {
          savedSelection.pages.push(i);
        }
      } else {
        savedSelection.pages.push(Number(pageRange[0]));
      }
      //loop through the positions and save which page each of the positions is located on
      positions.forEach(position => {
        const pageOfInterest = pageArray.find(page => {
          console.log(page, position);
          return page.top <= position.y && page.bottom >= position.y;
        });
        console.log(pageOfInterest);
        const thisPosition = {};
        //get the relative position of the element with regards to the page
        thisPosition.y = (position.y - pageOfInterest.top) / pageOfInterest.height;
        thisPosition.x = (position.x - pageOfInterest.left) / pageOfInterest.width;
        thisPosition.height = position.height / pageOfInterest.height;
        thisPosition.width = position.width / pageOfInterest.width;
        thisPosition.page = pageOfInterest.page;
        //add the selection and the page
        savedSelection.positions.push(thisPosition);
        // if (!savedSelection.pages.includes(pageOfInterest.page)) {
        //   savedSelection.pages.push(pageOfInterest.page);
        // }
      });

      const content = {
        excerpt: savedSelection.text,
        source: 'pdfViewer',
        pages: savedSelection.pages,
        positions: savedSelection.positions,
        callback: () => {
          console.log('call back!');
          // clear selection
          if (window.getSelection) {
            if (window.getSelection().empty) {
              // Chrome
              window.getSelection().empty();
            } else if (window.getSelection().removeAllRanges) {
              // Firefox
              window.getSelection().removeAllRanges();
            }
          }
          this.toggleExcerptBtn();
        }
      };
      console.log('emit saveexcerpt');
      this.$emit('saveExcerpt', content);
    },
    onResize() {
      console.log('rezie');
    },
    updatePagePosition() {
      const pagePosition = this.$refs.textLayer.getBoundingClientRect();
      pagePosition.page = this.pageNumber;
      console.log(`pagePosition-${this.pageNumber}`, pagePosition);
      this.setPagePosition({ page: this.pageNumber, position: pagePosition });
    },
    drawPage() {
      // console.log(`drawPage ${this.pageNumber}`);
      if (this.renderTask) return;

      const { viewport } = this;
      const canvasContext = document.getElementById(`render-${this.pageNumber}`).getContext('2d');
      const renderContext = { canvasContext, viewport };

      // PDFPageProxy#render
      // https://mozilla.github.io/pdf.js/api/draft/PDFPageProxy.html
      this.renderTask = this.page.render(renderContext);
      this.renderTask
        .then(() => {
          console.log('get annotations');
          this.page.getAnnotations().then(annotations => {
            console.log(annotations);
          });
          this.$emit('page-rendered', {
            page: this.page,
            text: `Rendered page ${this.pageNumber}`
          });
        })
        .catch(response => {
          this.destroyRenderTask();
          this.$emit('page-errored', {
            response,
            page: this.page,
            text: `Failed to render page ${this.pageNumber}`
          });
        });
    },

    updateVisibility() {
      this.$parent.$emit('update-visibility');
    },

    destroyPage(page) {
      // PDFPageProxy#_destroy
      // https://mozilla.github.io/pdf.js/api/draft/PDFPageProxy.html
      if (page) page._destroy();

      this.destroyRenderTask();
    },

    destroyRenderTask() {
      if (!this.renderTask) return;

      // RenderTask#cancel
      // https://mozilla.github.io/pdf.js/api/draft/RenderTask.html
      this.renderTask.cancel();
      delete this.renderTask;
    },
    async updateTextLayer() {
      // console.log(`updateTextLayer, page: ${this.pageNumber}`);
      const textLayerDiv = this.$refs['textLayer'];
      const textContent = await this.page.getTextContent();
      const viewport = this.anotherViewport;
      //clear all the other divs
      while (textLayerDiv.firstChild) {
        textLayerDiv.removeChild(textLayerDiv.firstChild);
      }

      // console.log('all the text layers are removed');

      // Pass the data to the method for rendering of text over the pdf canvas.
      try {
        await PDFJS.renderTextLayer({
          textContent: textContent,
          container: textLayerDiv,
          viewport: viewport,
          textDivs: []
        });
        // this.$nextTick(() => {
        //   this.updatePagePosition();
        // });
        return true;
      } catch (e) {
        throw new Error(e);
      }
      // console.log('renderResult', renderResult);
    },
    captureScreenShot(dimensions) {
      // console.log('dimensions', dimensions);
      const { minX, minY, width, height } = dimensions;
      const bufferCanvas1 = document.createElement('canvas');
      const textLayer = this.$refs['textLayer'];
      // console.log('this.$refs', this.$refs);
      // console.log('textLayer', textLayer);
      const originalCanvas = this.$refs['render'];
      console.log('render', originalCanvas.width, originalCanvas.height);
      console.log('textLayer', textLayer.offsetWidth, textLayer.offsetHeight);
      bufferCanvas1.width = textLayer.offsetWidth;
      bufferCanvas1.height = textLayer.offsetHeight;
      bufferCanvas1
        .getContext('2d')
        .drawImage(
          originalCanvas,
          0,
          0,
          originalCanvas.width,
          originalCanvas.height,
          0,
          0,
          bufferCanvas1.width,
          bufferCanvas1.height
        );

      // console.log('bufferCanvas1 has been drawn', bufferCanvas1);
      const bufferCanvas2 = document.createElement('canvas');
      bufferCanvas2.width = width;
      bufferCanvas2.height = height;
      bufferCanvas2
        .getContext('2d')
        .drawImage(bufferCanvas1, minX, minY, width, height, 0, 0, width, height);
      const savedSelection = {};
      savedSelection.url = bufferCanvas2.toDataURL('image/png');
      savedSelection.pages = [];
      savedSelection.positions = [];

      savedSelection.pages.push(this.pageNumber);
      const positions = {
        x: minX / bufferCanvas1.width,
        y: minY / bufferCanvas1.height,
        width: width / bufferCanvas1.width,
        height: height / bufferCanvas1.height,
        page: this.pageNumber
      };
      savedSelection.positions.push(positions);
      console.log('savedSelection', savedSelection);
      this.setCaptureCurrent(false);
      this.setCapturedImage(savedSelection);
    }
  },

  watch: {
    scale: 'updateVisibility',

    page(_newPage, oldPage) {
      this.destroyPage(oldPage);
    },

    isElementFocused(isElementFocused) {
      if (isElementFocused) this.focusPage();
    },

    overlayAttributes: function() {
      console.log('overlay attributes was modified');
      this.updateTextLayer();
    },

    captureCurrent(value) {
      console.log(this.finalShape);
      // console.log('capture', value);
      // console.log('this.pageNumber', this.pageNumber);
      // console.log('this.finalShape.page', this.finalShape.page);
      if (value && this.finalShape.page === this.pageNumber) {
        this.captureScreenShot(this.finalShape);
      }
    }
  },

  mounted() {
    log(`Page ${this.pageNumber} mounted`);
    this.updateTextLayer();
  },

  beforeDestroy() {
    this.destroyPage(this.page);
  }
};
</script>
<style>
.pdf-page {
  display: block;
  margin: 0 auto;
}

.overlay-page {
  position: absolute;
  margin: 0;
}

.page-canvas {
  pointer-events: none;
  mix-blend-mode: darken;
  z-index: 2;
}

.textLayer {
  overflow: hidden;
  opacity: 0.8;
  line-height: 1;
  direction: ltr;
}

.textLayer.screenShot {
  pointer-events: none;
}

.textLayer > span {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  transform-origin: 0% 0%;
  direction: ltr;
  /* height: 75px; */
}

.textLayer > span::selection {
  color: transparent;
}

.textLayer.screenShot {
  pointer-events: none;
}
</style>
